import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import type { RehydrateAction, RootState } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  DEFAULT_LAYERS,
  LIST_PAGE_SIZE,
  globalMapSliceName
} from "./global-map.constants"
import {
  fetchAnalytics,
  fetchFacets,
  fetchListRows,
  fetchMapFeatures,
  fetchQuickView,
  fetchRecentActivity
} from "./global-map.thunks"
import {
  EMPTY_FILTERS,
  type EntityType,
  type GlobalMapFilters,
  type GlobalMapState,
  type ListEntityTab,
  type ListSortField,
  type MapLayer,
  type MapViewport
} from "./global-map.types"

const initialState: GlobalMapState = {
  committedQuery: { layers: DEFAULT_LAYERS, filters: EMPTY_FILTERS },
  viewport: null,
  data: null,
  lastFingerprint: null,
  requestId: null,
  status: "idle",
  error: null,
  stale: false,
  selectedFeatureId: null,
  selectionSeq: 0,
  quickView: {
    data: null,
    status: "idle",
    requestId: null
  },
  activeTab: "map",
  searchTerm: "",
  facets: null,
  facetsRequestId: null,
  list: {
    data: null,
    status: "idle",
    error: null,
    requestId: null,
    listEntity: "all",
    sort: "relevant_date",
    direction: "desc",
    currentPage: 1,
    pageSize: LIST_PAGE_SIZE,
    selectedIds: []
  },
  analytics: {
    data: null,
    status: "idle",
    error: null,
    requestId: null
  },
  recentActivityInView: {
    data: null,
    status: "idle",
    error: null,
    requestId: null
  }
}

const globalMapSlice = createSlice({
  name: globalMapSliceName,
  initialState,
  reducers: {
    setViewport: (state, action: PayloadAction<MapViewport>) => {
      state.viewport = action.payload
    },

    // A selection change always resets `quickView` — otherwise a panel left
    // open while switching features, or reopened after a clear, could show
    // one feature's stale card under a different feature's id.
    //
    // `selectionSeq` bumps on EVERY dispatch here, including a reselect of
    // the SAME id — a real, reported bug: clicking a marker that's already
    // the open selection re-dispatches this with an unchanged `id`, so
    // `EntityQuickView`'s own fetch effect (keyed on its `projectId` prop)
    // never re-runs, even though `quickView.data` was just reset to `null`
    // two lines above — the panel goes permanently blank. `MapTab` passes
    // `selectionSeq` as `EntityQuickView`'s React `key`, forcing a full
    // remount (and therefore a fresh fetch) on every selection event, same
    // id or not.
    selectFeature: (
      state,
      action: PayloadAction<{ id: string; entityType: MapLayer }>
    ) => {
      state.selectedFeatureId = action.payload.id
      state.selectionSeq += 1
      state.quickView = { data: null, status: "idle", requestId: null }
    },

    clearSelection: (state) => {
      state.selectedFeatureId = null
      state.quickView = { data: null, status: "idle", requestId: null }
    },

    markStale: (state) => {
      state.stale = true
    },

    // The "Projects & Leads" filter (`GlobalMapFilterBar`) is deliberately
    // 0/1/2-choice — zero is a real, allowed selection (the map then shows
    // `MapNoResults` with a "reset" action, same as any other filter
    // combination that matches nothing), not refused here the way it used to
    // be when this was the toolbar's own always-on `LayerSelector` toggle.
    // Clears the List tab's row selection (Task 11) — `LayerToggleButtons`
    // dispatches this directly from the List tab's own Layers popover
    // (`GlobalMapToolbar`), and `useListFeatures.ts`'s `activeLayers` effect
    // already treats any change here as a new query shape, resetting
    // `currentPage` to 1 the same way.
    toggleLayer: (state, action: PayloadAction<MapLayer>) => {
      const layers = state.committedQuery.layers
      const isActive = layers.includes(action.payload)
      state.committedQuery.layers = isActive
        ? layers.filter((layer) => layer !== action.payload)
        : [...layers, action.payload]
      state.list.selectedIds = []
    },

    // Wholesale replace — its callers are `useGlobalMapUrlSync` (seeding the
    // store from a URL it has already parsed and validated — `parseLayersParam`
    // never actually hands this an empty array; see its own comment) and the
    // "Projects & Leads" filter, which can legitimately choose zero. See
    // `toggleLayer` above for why zero is no longer refused, and for why this
    // also clears the List tab's row selection.
    setActiveLayers: (state, action: PayloadAction<MapLayer[]>) => {
      state.committedQuery.layers = action.payload
      state.list.selectedIds = []
    },

    // Merges into the existing filters rather than replacing them, so a
    // caller setting one filter (e.g. from a facet click) never clobbers the
    // others already active. Also clears the List tab's row selection (Task
    // 11) — a filters change is a new query shape, same reasoning as
    // `setListEntity`/`setListSort`/`toggleListDirection` resetting
    // `currentPage` to 1 below.
    setFilters: (state, action: PayloadAction<Partial<GlobalMapFilters>>) => {
      state.committedQuery.filters = {
        ...state.committedQuery.filters,
        ...action.payload
      }
      state.list.selectedIds = []
      state.selectedFeatureId = null
      state.quickView = { data: null, status: "idle", requestId: null }
    },

    clearFilters: (state) => {
      state.committedQuery.filters = EMPTY_FILTERS
      state.selectedFeatureId = null
      state.quickView = { data: null, status: "idle", requestId: null }
    },

    // Search is intentionally outside committedQuery — see its comment in
    // global-map.types.ts — so this never touches committedQuery.filters.
    // Clears the List tab's row selection for the same reason `setFilters`
    // does above, and the Map tab's quick view for the same reason: a
    // changed query can leave a previously-selected record out of range,
    // and a stale quick view panel then reads as "Nearby found nothing"
    // for a record the new query never actually returned.
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload
      state.list.selectedIds = []
      state.selectedFeatureId = null
      state.quickView = { data: null, status: "idle", requestId: null }
    },

    setActiveTab: (
      state,
      action: PayloadAction<GlobalMapState["activeTab"]>
    ) => {
      state.activeTab = action.payload
    },

    // A sub-tab change is a brand-new query — page 1 again, same principle
    // `useMapFeatures.ts`'s filters-change effect already applies on the map
    // side. Also clears the row selection (Task 11) for the same reason.
    setListEntity: (state, action: PayloadAction<ListEntityTab>) => {
      state.list.listEntity = action.payload
      state.list.currentPage = 1
      state.list.selectedIds = []
    },

    setListSort: (state, action: PayloadAction<ListSortField>) => {
      state.list.sort = action.payload
      state.list.currentPage = 1
      state.list.selectedIds = []
    },

    toggleListDirection: (state) => {
      state.list.direction = state.list.direction === "asc" ? "desc" : "asc"
      state.list.currentPage = 1
      state.list.selectedIds = []
    },

    // Page-number pagination (2026-09-20 backend contract change, replacing
    // cursor pagination) — dispatched by `useListFeatures.ts`'s `setPage`,
    // itself called directly or (Task 9) from a pagination footer. Deliberately
    // does NOT clear `selectedIds` — paging within the SAME query shape lets a
    // cross-page selection survive, since Task 13's Export button reads it.
    setListPage: (state, action: PayloadAction<number>) => {
      state.list.currentPage = action.payload
    },

    // Task 11 — antd `Table`'s own `rowSelection.onChange` hands this the
    // FULL current set of checked keys (not a delta), so this is a plain
    // replace, mirroring `setActiveLayers` above.
    setListSelection: (state, action: PayloadAction<string[]>) => {
      state.list.selectedIds = action.payload
    },

    // Wired into `GlobalMapFilterBar`'s "Clear All" handler, alongside
    // `clearFilters`/`setListEntity("all")` — a genuinely fresh List query,
    // so any prior selection should be dropped too.
    clearListSelection: (state) => {
      state.list.selectedIds = []
    },

    // Task 9 — the client's own chosen page size. A plain setter, deliberately
    // with no side effect on `currentPage`: `useGlobalMapUrlSync`'s mount-time
    // URL hydration and `useListFeatures`'s persisted-preference fallback both
    // need to set this WITHOUT disturbing whatever page the user is on (a
    // `?page=3&pageSize=20` deep link must land on page 3 at size 20, not
    // reset to page 1). A genuine user-driven size change resetting the page
    // is instead the caller's own responsibility — see
    // `ListPaginationFooter.tsx`'s `onChange`, which calls `onPageChange(1)`
    // right alongside `onPageSizeChange`.
    setListPageSize: (state, action: PayloadAction<number>) => {
      state.list.pageSize = action.payload
    }
  },
  extraReducers: (builder) => {
    // Only `committedQuery` is persisted; everything server-derived restarts
    // from initialState so a filters-only payload cannot hollow out the slice.
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const stored = action?.payload?.[globalMapSliceName]
      if (!stored) return state

      return {
        ...initialState,
        committedQuery: {
          ...initialState.committedQuery,
          ...stored.committedQuery,
          // A shallow spread of `stored.committedQuery` alone would replace
          // `filters` wholesale with whatever shape was persisted — a real
          // browser session persisted from before the four relationship
          // filters widened from single-id to multi-id ships a `filters`
          // object with no `salespersonIds`/etc. arrays at all, which
          // crashed `FacetMultiSelectFilter` (`values.map(...)` on
          // `undefined`) the moment this rehydrated state reached it. One
          // more level of merge backfills any field the persisted shape is
          // missing from the current `EMPTY_FILTERS`, the same deploy-window
          // safety this case already gave `committedQuery` itself.
          filters: { ...EMPTY_FILTERS, ...stored.committedQuery?.filters }
        }
      }
    })

    builder
      .addCase(fetchMapFeatures.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.status = state.data ? "refreshing" : "loading"
        state.error = null
      })
      .addCase(fetchMapFeatures.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return

        state.data = action.payload
        state.lastFingerprint = action.payload.query_fingerprint
        state.status = "idle"
        state.stale = false
        state.error = null
      })
      .addCase(fetchMapFeatures.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return

        state.status = "error"
        state.error = action.payload ?? {
          code: "request_failed",
          retryable: true
        }
      })

    // Same stale-response guard shape as `fetchMapFeatures` above: an older
    // facets request resolving after a newer one (two filter changes fired in
    // quick succession) is dropped rather than overwriting the newer counts.
    // A rejection is silently ignored either way — facets are display-only
    // filter-UI data, not something the map surfaces its own error state for;
    // the pickers just keep whatever options (possibly none yet) they had.
    builder
      .addCase(fetchFacets.pending, (state, action) => {
        state.facetsRequestId = action.meta.requestId
      })
      .addCase(fetchFacets.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.facetsRequestId) return
        state.facets = action.payload
      })
      // Symmetry with `fetchMapFeatures.rejected`'s own stale-guarded requestId
      // clear — without this, a failed facets fetch leaves `facetsRequestId`
      // pointed at a request that will never resolve. Harmless today (nothing
      // reads facets-in-flight status), but a later caller that ever does
      // would otherwise see a permanently "in flight" facets request after
      // any failure.
      .addCase(fetchFacets.rejected, (state, action) => {
        if (action.meta.requestId !== state.facetsRequestId) return
        state.facetsRequestId = null
      })

    builder
      .addCase(fetchQuickView.pending, (state, action) => {
        state.quickView.requestId = action.meta.requestId
        state.quickView.status = "loading"
      })
      .addCase(fetchQuickView.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.quickView.requestId) return

        state.quickView.data = action.payload
        state.quickView.status = "idle"
      })
      .addCase(fetchQuickView.rejected, (state, action) => {
        if (action.meta.requestId !== state.quickView.requestId) return

        state.quickView.status = "error"
      })

    // Same stale-response guard shape as `fetchMapFeatures` above, applied to
    // the List tab's own `requestId` — a sub-tab/sort/direction change (or a
    // Next/Previous page turn) can fire a new request before an older one
    // resolves, and the older one must not overwrite the newer page's rows.
    builder
      .addCase(fetchListRows.pending, (state, action) => {
        state.list.requestId = action.meta.requestId
        state.list.status = state.list.data ? "refreshing" : "loading"
        state.list.error = null
      })
      .addCase(fetchListRows.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.list.requestId) return

        state.list.data = action.payload
        state.list.status = "idle"
        state.list.error = null
      })
      .addCase(fetchListRows.rejected, (state, action) => {
        if (action.meta.requestId !== state.list.requestId) return

        state.list.status = "error"
        state.list.error = action.payload ?? {
          code: "request_failed",
          retryable: true
        }
      })

    // Same stale-response guard shape as `fetchListRows` above.
    builder
      .addCase(fetchAnalytics.pending, (state, action) => {
        state.analytics.requestId = action.meta.requestId
        state.analytics.status = state.analytics.data ? "refreshing" : "loading"
        state.analytics.error = null
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.analytics.requestId) return

        state.analytics.data = action.payload
        state.analytics.status = "idle"
        state.analytics.error = null
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        if (action.meta.requestId !== state.analytics.requestId) return

        state.analytics.status = "error"
        state.analytics.error = action.payload ?? {
          code: "request_failed",
          retryable: true
        }
      })

    // Same stale-response guard shape as `fetchAnalytics` above.
    builder
      .addCase(fetchRecentActivity.pending, (state, action) => {
        state.recentActivityInView.requestId = action.meta.requestId
        state.recentActivityInView.status = state.recentActivityInView.data
          ? "refreshing"
          : "loading"
        state.recentActivityInView.error = null
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.recentActivityInView.requestId)
          return

        state.recentActivityInView.data = action.payload
        state.recentActivityInView.status = "idle"
        state.recentActivityInView.error = null
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        if (action.meta.requestId !== state.recentActivityInView.requestId)
          return

        state.recentActivityInView.status = "error"
        state.recentActivityInView.error = action.payload ?? {
          code: "request_failed",
          retryable: true
        }
      })

    builder.addCase(logout.fulfilled, () => initialState)
  }
})

export const {
  setViewport,
  selectFeature,
  clearSelection,
  markStale,
  toggleLayer,
  setActiveLayers,
  setFilters,
  clearFilters,
  setSearchTerm,
  setActiveTab,
  setListEntity,
  setListSort,
  toggleListDirection,
  setListPage,
  setListPageSize,
  setListSelection,
  clearListSelection
} = globalMapSlice.actions

export const selectMapData = (state: RootState) => state.globalMap.data

export const selectMapStatus = (state: RootState) => state.globalMap.status

export const selectSelectedFeatureId = (state: RootState) =>
  state.globalMap.selectedFeatureId

export const selectSelectionSeq = (state: RootState) =>
  state.globalMap.selectionSeq

export const selectQuickView = (state: RootState) => state.globalMap.quickView

export const selectMapFallbackCenter = (state: RootState) =>
  state.globalMap.data?.fallback_center ?? null

export const selectActiveLayers = (state: RootState) =>
  state.globalMap.committedQuery.layers

export const selectActiveFilters = (state: RootState) =>
  state.globalMap.committedQuery.filters

export const selectSearchTerm = (state: RootState) => state.globalMap.searchTerm

export const selectFacets = (state: RootState) => state.globalMap.facets

export const selectActiveTab = (state: RootState) => state.globalMap.activeTab

export const selectListState = (state: RootState) => state.globalMap.list

export const selectListEntity = (state: RootState) =>
  state.globalMap.list.listEntity

export const selectListSort = (state: RootState) => state.globalMap.list.sort

export const selectListDirection = (state: RootState) =>
  state.globalMap.list.direction

export const selectListCurrentPage = (state: RootState) =>
  state.globalMap.list.currentPage

export const selectListPageSize = (state: RootState) =>
  state.globalMap.list.pageSize

export const selectListSelection = (state: RootState) =>
  state.globalMap.list.selectedIds

export const selectAnalyticsState = (state: RootState) =>
  state.globalMap.analytics

export const selectRecentActivityInViewState = (state: RootState) =>
  state.globalMap.recentActivityInView

export default globalMapSlice
